import { useEffect, useRef, useState } from 'react';
import useModal from '../../../hooks/use-close-modal.hook';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import Button from '../../../components/forms/button.component';
import Select from 'react-select';
import InvalidFeedback from '../../../components/forms/invalid-feedback.component';
import { AlertService } from '../../../services/alert.service';
import { CompaniesService } from '../../../services/companies.service';
import { AccountsService } from '../../../services/accounts.service';
import { RolesService } from '../../../services/roles.service';
import LogData from '../../../components/logs/log-data.component';
import Icon from '../../../components/forms/icon.component';
interface DuplicateRolesProps {
  show: boolean;
  onRequestClose: (refresh: boolean) => void;
  payload?: any;
}
function DuplicateRoles({ show, onRequestClose, payload }: DuplicateRolesProps) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  useModal(modalRef, show);
  const [company, setCompany] = useState<any>([]);
  const [account, setAccount] = useState<any>([]);
  const [selectedAccountName, setSelectedAccountName] = useState<string | null>(null);
  const defaultValues = {
    account: '',
    role: '',
    company: ''
  };
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isValid, isSubmitting }
  } = useForm({
    mode: 'onChange',
    defaultValues
  });
  const getCompanies = async () => {
    try {
      const data = await CompaniesService.getCompanies();
      const mappedCompanies = data.map((company: any) => ({
        value: company._id,
        label: company.name
      }));
      setCompany(mappedCompanies);
    } catch (errorCode: any) {
      console.log(errorCode);
      AlertService.toastError(errorCode || errorCode.message);
    }
  };
  const getAccounts = async (companyId: string) => {
    const data = await AccountsService.getAccountsForCompany(companyId);
    const mappedAccounts = data.map((account: any) => ({
      value: account._id,
      label: account.name
    }));
    setAccount(mappedAccounts);
  };
  const submit = async (data: any, buttonId: string) => {
    delete data.company;
    const isConfirmed = await AlertService.question(
      t('general.atention'),
      t('roles.role-duplicate-alert'),
      () => Promise.resolve(true)
    );
    if (!isConfirmed) return;
    try {
      await RolesService.duplicateRoles(data);
      AlertService.toastSuccess(t('roles.role-duplicate-success'));
      onRequestClose(true);
      setSelectedAccountName(null);
      LogData(data, buttonId, window.location.href);
    } catch (errorCode: any) {
      console.log(errorCode);
      AlertService.toastError(errorCode || errorCode.message);
    }
  };
  const closeModal = () => {
    onRequestClose(false);
    reset(defaultValues);
    setSelectedAccountName(null);
  };

  useEffect(() => {
    show && getCompanies();
  }, [show]);
  useEffect(() => {
    watch('company') !== '' && getAccounts(watch('company'));
  }, [watch('company'), watch('account')]);

  useEffect(() => {
    if (payload) {
      const companyId = payload?.account?.company?._id || '';
      const companyName = payload?.account?.company?.name || '';
      const accountId = payload?.account?._id || '';
      const accountName = payload?.account?.name || '';
      const roleId = payload?._id || '';
      if (roleId) {
        setValue('role', roleId);
      }

      if (companyId) {
        setCompany([{ value: companyId, label: companyName }]);
        setValue('company', companyId);
        getAccounts(companyId);
      }
      if (accountId) {
        setAccount([{ value: accountId, label: accountName }]);
        setValue('account', accountId);
      }
    }
  }, [payload]);

  return (
    <div className="modal fade" ref={modalRef} data-bs-backdrop="static">
      <div className="modal-dialog mw-650px">
        <div className="modal-content">
          <div className="modal-header justify-content-end border-0 pb-0">
            <div
              className="btn btn-sm btn-icon btn-active-light-primary"
              onClick={() => closeModal()}
            >
              <Icon icon="ki-cross" size="fs-2x" />
            </div>
          </div>
          <form onSubmit={handleSubmit((data) => submit(data, 'duplicateRol-7364093748'))}>
            <div className="modal-body pt-0 pb-15 px-5 px-xl-20">
              <div className="mt-1 text-center">
                <h1 className="mb-3">
                  {t('roles.duplicate-role')} <br />
                  <a className="link-primary fw-bold ms-2">{payload?.name}</a>
                </h1>
                <div className="text-muted fw-semibold fs-5">
                  <span
                    dangerouslySetInnerHTML={{ __html: t('channels.rcs.modal-C-Subtitle') }}
                  ></span>
                </div>
              </div>
              <div className="separator border-2 my-5"></div>

              <div className="d-flex justify-content-center row mb-10">
                <div className="col-lg-8 mb-10 mb-lg-0  h-100 p-10">
                  <div className="fv-row mb-7">
                    <label className="form-label required">
                      {t('blacklist-page.company-label')}
                    </label>
                    <Controller
                      control={control}
                      name="company"
                      rules={{ required: true }}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <>
                          <Select
                            isDisabled={payload}
                            options={company}
                            noOptionsMessage={({ inputValue }) =>
                              inputValue
                                ? t('general.no-options', { inputValue })
                                : t('general.no-options')
                            }
                            className={
                              error ? 'form-select-custom is-invalid' : 'form-select-custom'
                            }
                            classNamePrefix="form-select-custom"
                            placeholder={t('blacklist-page.company-placeholder')}
                            onChange={(selectedOption: { value: string; label: string } | null) => {
                              onChange(selectedOption ? selectedOption.value : null);
                              setValue('company', selectedOption ? selectedOption.value : '');
                            }}
                            value={company.find((company: any) => company.value === value) || null}
                          />
                          <InvalidFeedback error={error} />
                        </>
                      )}
                    />
                  </div>
                  <div className="row">
                    <label className="form-label required">{t('roles.account-label')}</label>
                    <Controller
                      control={control}
                      name="account"
                      rules={{ required: true }}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <>
                          <Select
                            options={account}
                            noOptionsMessage={({ inputValue }) =>
                              inputValue
                                ? t('general.no-options', { inputValue })
                                : t('general.no-options')
                            }
                            className={
                              error ? 'form-select-custom is-invalid' : 'form-select-custom'
                            }
                            classNamePrefix="form-select-custom"
                            placeholder={t('roles.account-placeholder')}
                            onChange={(selectedOption: { value: string; label: string } | null) => {
                              onChange(selectedOption ? selectedOption.value : null);
                              setValue('account', selectedOption ? selectedOption.value : '');
                              if (selectedOption?.value === payload?.account?._id) {
                                setSelectedAccountName(null);
                              } else {
                                setSelectedAccountName(
                                  selectedOption ? selectedOption.label : null
                                );
                              }
                            }}
                            value={account.find((acc: any) => acc.value === value) || null}
                          />
                          <InvalidFeedback error={error} />
                        </>
                      )}
                    />
                  </div>
                  {selectedAccountName && payload?.account?.name && (
                    <div className="alert alert-info mt-10 text-center py-10" role="alert">
                      <Icon icon="ki-information" size="fs-3" type="primary" />
                      <h4 className="text-info f-bold">{t('roles.duplicate-role')}</h4>
                      <p>
                        {t('roles.role-duplicate-message-prefix')}{' '}
                        <strong>{payload.account.name}</strong>{' '}
                        {t('roles.role-duplicate-message-middle')}{' '}
                        <strong>{selectedAccountName}</strong>
                        {'.'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div className="d-flex flex-center flex-row-fluid">
                <button
                  type="button"
                  className="btn btn-light hover-scale"
                  onClick={() => closeModal()}
                >
                  {t('general.close')}
                </button>
                <Button
                  type="submit"
                  id="sendData"
                  className="btn btn-primary ms-3 hover-scale"
                  disabled={!isValid || watch('account') === payload?.account?._id}
                  isLoading={isSubmitting}
                >
                  {t('general.duplicated')} <Icon icon="ki-send" size="fs-1" ClassName="ms-2" />
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default DuplicateRoles;
